export const DEFAULT_ATTRIBUTION_WINDOWS = [7, 14, 30].map(days => ({
  id: String(days),
  days,
  label: `${days} days`
}));

const getId = item => (typeof item === 'object' ? item?.id || item?.value : item);
const getLabel = item => (typeof item === 'object' ? item?.displayName || item?.label || getId(item) : item);
const getAttributionWindowDays = attributionWindow =>
  attributionWindow?.days ?? Number(attributionWindow?.id);

export const WEB_PROPERTY_UPDATE_MASK = [
  'displayName',
  'destinationUrl',
  'campaignIds',
  'attributionWindowDays'
];

export const normalizeOption = item => ({
  value: getId(item),
  label: getLabel(item),
  data: typeof item === 'object' ? item : undefined
});

export const normalizeSelections = (items = [], fallbackIds = []) =>
  (items.length ? items : fallbackIds).filter(Boolean).map(normalizeOption);

export const normalizeAttributionWindows = response => {
  const suppliedWindows =
    response?.attributionWindows ||
    response?.supportedAttributionWindows ||
    response?.configuration?.attributionWindows ||
    response?.config?.attributionWindows;

  if (!Array.isArray(suppliedWindows) || suppliedWindows.length === 0) return DEFAULT_ATTRIBUTION_WINDOWS;

  return suppliedWindows.map(window => {
    const days = typeof window === 'object' ? window.days ?? window.value ?? window.id : window;
    return {
      ...(typeof window === 'object' ? window : {}),
      id: String(days),
      days: Number(days),
      label: typeof window === 'object' && window.label ? window.label : `${days} days`
    };
  });
};

const normalizeAttributionWindow = (value, supportedWindows) => {
  if (value === null || typeof value === 'undefined' || value === '') return null;
  const selectedValue = typeof value === 'object' ? value.days ?? value.value ?? value.id : value;
  return (
    supportedWindows.find(window => String(window.id) === String(selectedValue)) || {
      id: String(selectedValue),
      days: Number(selectedValue),
      label: `${selectedValue} days`
    }
  );
};

export const getWebPropertiesFromResponse = response => {
  return Array.isArray(response?.webProperties) ? response.webProperties : [];
};

export const getWebPropertyFromResponse = response => {
  const webProperty = response?.webProperty || response?.webproperty;
  return webProperty || response;
};

export const hasTrackingData = webEmbed =>
  Boolean(
    webEmbed?.hasTrackingData ||
      webEmbed?.trackingDataReceived ||
      webEmbed?.firstEventTime ||
      webEmbed?.lastEventTime ||
      webEmbed?.lastTrackingDataTime ||
      Number(webEmbed?.eventCount) > 0
  );

export const isConnectionActive = webEmbed => {
  const state = String(webEmbed?.connectionState || webEmbed?.status || '').toUpperCase();
  return hasTrackingData(webEmbed) || state.includes('ACTIVE') || state.includes('CONNECTED');
};

export const getDisplayStatus = webEmbed => (isConnectionActive(webEmbed) ? 'Active' : 'Pending Data');

export const normalizeWebEmbed = (webEmbed, supportedWindows = DEFAULT_ATTRIBUTION_WINDOWS) => {
  return {
    ...webEmbed,
    id: webEmbed?.id || '',
    displayName: webEmbed?.displayName || '',
    destinationUrl: webEmbed?.destinationUrl || '',
    ingestionKey: webEmbed?.ingestionKey || '',
    embedSnippet: webEmbed?.embedSnippet || '',
    qrTrackingUrl: webEmbed?.qrTrackingUrl || '',
    status: webEmbed?.status || '',
    name: webEmbed?.displayName || '',
    campaigns: normalizeSelections(webEmbed?.campaigns, webEmbed?.campaignIds),
    attributionWindow: normalizeAttributionWindow(
      webEmbed?.attributionWindow || webEmbed?.attributionWindowDays,
      supportedWindows
    ),
    troubleshootingUrl: webEmbed?.troubleshootingUrl || webEmbed?.helpUrl || '',
    connectionState: isConnectionActive(webEmbed) ? 'active' : 'pending'
  };
};

export const createEmptyWebEmbed = supportedWindows => ({
  name: '',
  campaigns: [],
  attributionWindow: supportedWindows?.[0] || null,
  destinationUrl: '',
  ingestionKey: '',
  qrTrackingUrl: '',
  embedSnippet: '',
  status: '',
  connectionState: 'pending'
});

const UNSAFE_DESTINATION_PROTOCOLS = new Set(['blob:', 'data:', 'file:', 'javascript:', 'vbscript:']);
const URL_PROTOCOL_PATTERN = /^[a-z][a-z\d+.-]*:/i;
const HOST_WITH_PORT_PATTERN = /^[^/?#]+:\d+(?:[/?#]|$)/;

const hasExplicitProtocol = value =>
  URL_PROTOCOL_PATTERN.test(value) && !HOST_WITH_PORT_PATTERN.test(value);

const isValidHostname = hostname => {
  if (hostname === 'localhost' || (hostname.startsWith('[') && hostname.endsWith(']'))) return true;

  const labels = hostname.split('.');
  return (
    labels.length > 1 &&
    labels.every(label => /^[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?$/i.test(label))
  );
};

export const normalizeDestinationUrl = value => {
  const destinationUrl = String(value || '').trim();
  if (!destinationUrl || hasExplicitProtocol(destinationUrl)) return destinationUrl;

  return `https://${destinationUrl}`;
};

export const isValidDestinationUrl = value => {
  try {
    const destinationUrl = String(value || '').trim();
    const includesProtocol = hasExplicitProtocol(destinationUrl);
    const url = new URL(normalizeDestinationUrl(destinationUrl));
    const protocol = url.protocol.toLowerCase();

    if (!protocol || UNSAFE_DESTINATION_PROTOCOLS.has(protocol)) return false;
    if (!includesProtocol) return isValidHostname(url.hostname);

    return true;
  } catch {
    return false;
  }
};

export const getWebPropertyPayload = (webEmbed, brandPortalId) => ({
  attributionWindowDays: getAttributionWindowDays(webEmbed.attributionWindow),
  brandPortalId,
  campaignIds: webEmbed.campaigns.map(getId),
  destinationUrl: normalizeDestinationUrl(webEmbed.destinationUrl),
  displayName: webEmbed.name.trim()
});

export const isVisibleWebEmbed = webEmbed => {
  const status = String(webEmbed?.status || '').toUpperCase();
  return webEmbed?.deleted !== true && !status.includes('DELETED');
};
